import { state, style, transition, animate, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CardType } from '../../../../global/services/user-cards/user-cards.model';
import { Subject } from 'rxjs';
import { FESubscription, UserOrgSubscriptionMessage, UserOrgSubscriptionMessageType } from '../../../user.model';
import * as moment from 'moment';

export enum ViewDetailsState {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
}

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.scss'],
    animations: [
        trigger('toggleDetails', [
            state(
                ViewDetailsState.OPEN,
                style({
                    height: '*',
                    opacity: '1',
                    'padding-top': '1rem',
                }),
            ),
            state(
                ViewDetailsState.CLOSED,
                style({
                    height: '0px',
                    opacity: '0',
                    'padding-top': 0,
                }),
            ),
            transition(`${ViewDetailsState.OPEN} <=> ${ViewDetailsState.CLOSED}`, animate(200)),
        ]),
    ],
})
export class SubscriptionComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    ViewDetailsState = ViewDetailsState;

    _showDetails = ViewDetailsState.CLOSED;

    @Input() public item: FESubscription;
    @Input() public selectable = false;
    @Input() public disableSelect = false;
    @Input() public showContextMenu = false;
    @Input() masterForm: FormGroup;
    @Output() detailsDialogPrompt = new EventEmitter<FESubscription>();
    @Output() deleteDialogPrompt = new EventEmitter<FESubscription>();
    @Output() renewDialogPrompt = new EventEmitter<FESubscription>();

    errors: UserOrgSubscriptionMessage[] = [];
    warnings: UserOrgSubscriptionMessage[] = [];

    form = new FormGroup({ selected: new FormControl(false) });

    get showDetails(): ViewDetailsState {
        return this._showDetails;
    }
    set showDetails(v: ViewDetailsState) {
        this._showDetails = v;
    }

    get code(): string {
        return this.item.inheritanceCode ? this.item.inheritanceCode : this.item.code;
    }

    get isRenewalAllowed(): boolean {
        if (!this.item?.endDate || !this.item.isRenewable) return false;
        const endOfDay = moment.utc(this.item.endDate).endOf('day');
        const monthsUntilEnd = endOfDay.diff(moment.utc(), 'months', true);
        return monthsUntilEnd <= 2;
    }

    ngOnInit() {
        this.item.messages.forEach((m) => {
            if (m.type === UserOrgSubscriptionMessageType.ERROR) this.errors.push(m);
            if (m.type === UserOrgSubscriptionMessageType.WARNING) this.warnings.push(m);
        });
    }

    handleSelectToggle(e: any) {
        if (e.target) {
            e = e.target.checked;
        } // app-form-checkbox sending two object types, need to handle "event" type TODO: fix this
        const update = {};
        update[this.item.code] = e;
        this.masterForm.patchValue(update);
    }

    openManagementDialog(item: FESubscription) {
        this.detailsDialogPrompt.emit(item);
    }

    openDeleteDialog(item: FESubscription) {
        this.deleteDialogPrompt.emit(item);
    }

    onRenewClick() {
        this.renewDialogPrompt.emit(this.item);
    }

    toggleShowDetails() {
        if (this.showDetails === ViewDetailsState.CLOSED) {
            this.showDetails = ViewDetailsState.OPEN;
        } else {
            this.showDetails = ViewDetailsState.CLOSED;
        }
    }

    getCardIcon(item: FESubscription) {
        switch (item.cardType) {
            case CardType.BCITI:
            case CardType.LEGACY:
            case CardType.CITIZEN:
            case CardType.SPORTS:
            case CardType.LIBRARY:
                return item.cardIcon;
            default:
                return 'icon-smart';
        }
    }

    getWrapCssClasses() {
        let classes = '';
        if (this.selectable) {
            classes += 'inactive';
        } else {
            classes += 'active';
        }
        classes += ` ${this.item.cardType}`;
        return classes;
    }

    formatDateUTC(date: string): string {
        const options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        };

        if (!date) {
            return '';
        }

        return new Intl.DateTimeFormat('en-CA', options).format(new Date(date));
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
